<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Список товаров и&nbsp;их данные</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="spisok-tovarov"
            class="typography__headline2"
          >
            Список товаров
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Товары&raquo;.</p>

            <p>
              Сверху справа можно добавить новый товар или перейти к&nbsp;управлению категориями, нажав соответствующие
              кнопки.
            </p>

            <p>
              В&nbsp;списке есть важная информация о&nbsp;товаре, возможность быстро скрыть его с&nbsp;сайта или перейти
              к&nbsp;редактированию, дублированию или удалению.
            </p>

            <p>
              Если у&nbsp;товара есть варианты&nbsp;&mdash; это будет подписано. По&nbsp;нажатию, снизу можно будет
              увидеть все варианты, их&nbsp;стоимость и&nbsp;остатки.
            </p>

            <p>По&nbsp;нажатию на&nbsp;название товара перейдем к&nbsp;его странице.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-tovarov-i-ikh-dannye-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>Над списком есть сортировка, фильтры и&nbsp;поисковая строка для быстрого поиска нужной услуги.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-tovarov-i-ikh-dannye-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="stranica-tovara"
            class="typography__headline2"
          >
            Страница товара
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              На&nbsp;открывшейся странице товара сверху можно увидеть его название. Под названием быстрый доступ
              к&nbsp;редактированию, дублированию или удалению.
            </p>

            <p>Ниже находится вся базовая информация о&nbsp;товаре и&nbsp;его галерея.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-tovarov-i-ikh-dannye-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>Далее находится блок с&nbsp;параметрами товара.</p>

            <p>
              Если у&nbsp;товара есть варианты&nbsp;&mdash; ниже мы&nbsp;увидим все их&nbsp;данные и&nbsp;отличные
              значения параметров.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/spisok-tovarov-i-ikh-dannye-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С добавлением товаров их просмотром разобрались. Самое время перейти к изучению
            <BaseLink
              class="ui-link-secondary"
              to="/faq/articles/tsifrovye-tovary"
              >цифровых товаров</BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Заказы с товарами",
    url: "/faq/categories/zakazy-s-tovarami",
  },
  {
    name: "Список товаров и&nbsp;их данные",
  },
];

const menu = [
  {
    name: "Список&nbsp;товаров",
    url: "spisok-tovarov",
  },
  {
    name: "Страница&nbsp;товара",
    url: "stranica-tovara",
  },
];

const relatedArticles = [
  {
    name: "Способы доставки",
    slug: "sposoby-dostavki",
  },
  {
    name: "Категории товаров",
    slug: "kategorii-tovarov",
  },
  {
    name: "Добавление и&nbsp;редактирование товара",
    slug: "dobavlenie-i-redaktirovanie-tovara",
  },
  {
    name: "Цифровые товары",
    slug: "tsifrovye-tovary",
  },
  {
    name: "Список заказов и&nbsp;их данные",
    slug: "spisok-zakazov-i-ikh-dannye",
  },
  {
    name: "Добавление и&nbsp;редактирование заказа",
    slug: "dobavlenie-i-redaktirovanie-zakaza",
  },
];

useSeoMeta({
  title: "Список товаров и их данные",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
